/**
 * Security utility functions for input validation and sanitization
 */

/**
 * Sanitizes a string to prevent XSS attacks
 * Escapes HTML special characters
 * @param input - The string to sanitize
 * @returns Sanitized string
 */
export const sanitizeHtml = (input: string | null | undefined): string => {
  if (input === null || input === undefined) {
    return '';
  }

  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;',
  };

  return String(input).replace(/[&<>"'`=/]/g, (char) => htmlEscapeMap[char] || char);
};

/**
 * Sanitizes user input by trimming whitespace and removing potentially dangerous characters
 * @param input - The string to sanitize
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string | null | undefined): string => {
  if (input === null || input === undefined) {
    return '';
  }

  return String(input)
    .trim()
    // Remove null bytes
    .replace(/\0/g, '')
    // Remove control characters except newlines and tabs
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
};

/**
 * Validates and sanitizes a URL to prevent open redirect vulnerabilities
 * Only allows relative URLs or URLs from allowed domains
 * @param url - The URL to validate
 * @param allowedDomains - List of allowed domains (optional)
 * @returns Sanitized URL or '/' if invalid
 */
export const sanitizeUrl = (
  url: string | null | undefined,
  allowedDomains: string[] = []
): string => {
  if (!url) {
    return '/';
  }

  const trimmedUrl = url.trim();

  // Allow relative URLs starting with /
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
    // Prevent path traversal
    const normalized = trimmedUrl.replace(/\/+/g, '/');
    if (!normalized.includes('..')) {
      return normalized;
    }
  }

  // For absolute URLs, check if domain is allowed
  try {
    const urlObj = new URL(trimmedUrl);

    // Block javascript: and data: URLs
    if (['javascript:', 'data:', 'vbscript:'].includes(urlObj.protocol)) {
      return '/';
    }

    // Check if domain is in allowed list
    if (allowedDomains.length > 0) {
      const isAllowed = allowedDomains.some(
        (domain) =>
          urlObj.hostname === domain ||
          urlObj.hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        return trimmedUrl;
      }
    }
  } catch {
    // Invalid URL
  }

  return '/';
};

/**
 * Validates email format
 * @param email - The email to validate
 * @returns Boolean indicating if email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates phone number format (basic validation)
 * @param phone - The phone number to validate
 * @returns Boolean indicating if phone is valid
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  // Remove common separators and check if remaining chars are digits and optional +
  const cleaned = phone.replace(/[\s\-().]/g, '');
  return /^\+?[0-9]{8,15}$/.test(cleaned);
};

/**
 * Masks sensitive data for logging (e.g., email, phone)
 * @param value - The value to mask
 * @param visibleChars - Number of characters to show at start (default: 3)
 * @returns Masked string
 */
export const maskSensitiveData = (
  value: string | null | undefined,
  visibleChars: number = 3
): string => {
  if (!value) {
    return '***';
  }

  const str = String(value);
  if (str.length <= visibleChars) {
    return '*'.repeat(str.length);
  }

  return str.substring(0, visibleChars) + '*'.repeat(Math.min(str.length - visibleChars, 10));
};

/**
 * Safely parses JSON without throwing
 * @param json - JSON string to parse
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed object or fallback
 */
export const safeJsonParse = <T>(json: string, fallback: T): T => {
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
};

/**
 * Rate limiting helper - checks if enough time has passed since last action
 * @param lastActionTime - Timestamp of last action
 * @param minIntervalMs - Minimum interval in milliseconds
 * @returns Boolean indicating if action is allowed
 */
export const isRateLimitAllowed = (
  lastActionTime: number | null,
  minIntervalMs: number
): boolean => {
  if (!lastActionTime) {
    return true;
  }
  return Date.now() - lastActionTime >= minIntervalMs;
};
